// RamsDashboardPage — Phase 2 internal RAMS request dashboard: summary cards,
// filters, and the request table. Issuing/duplicating/archiving happens here;
// the client-facing questionnaire runtime ships in Phase 3.
import { useCallback, useEffect, useState } from "react";
import { Plus, Loader2, Copy, Archive as ArchiveIcon, Send, AlertTriangle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Pill from "@/components/Pill";
import RamsIssueDialog from "@/components/rams/RamsIssueDialog";
import RamsRequestDetail from "@/components/rams/RamsRequestDetail";

const CAN_EDIT_ROLES = new Set(["director", "administrator", "consultant"]);
const STATUS_TONE = {
    "Draft Request": "neutral", "Questionnaire Issued": "amber", "Opened": "amber", "In Progress": "amber",
    "Submitted": "blue", "Information Review": "blue", "Clarification Required": "red",
    "Ready to Generate": "blue", "AI Draft Generated": "blue", "Priority Risk Review": "blue",
    "Approved": "green", "Published to Client": "green", "Client Acknowledged": "green",
    "Superseded": "neutral", "Archived": "neutral",
};

const CARDS = [
    ["draft", "Draft"], ["awaiting_client", "Awaiting client"], ["submitted", "Submitted"],
    ["clarification_required", "Clarification required"], ["awaiting_review", "Awaiting PR review"],
    ["approved", "Approved"], ["overdue", "Overdue"],
];

export default function RamsDashboardPage({ presetClientId, presetProjectId, compact }) {
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({});
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ status: "", overdue: "" });
    const [issueOpen, setIssueOpen] = useState(false);
    const [directOpen, setDirectOpen] = useState(false);
    const [detailId, setDetailId] = useState(null);
    const { user } = useAuth();
    const canEdit = user && CAN_EDIT_ROLES.has(user.role);

    const params = { ...(presetClientId ? { client_id: presetClientId } : {}), ...(presetProjectId ? { project_id: presetProjectId } : {}) };

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const q = { ...params };
            if (filters.status) q.status = filters.status;
            if (filters.overdue) q.overdue = true;
            const [{ data: list }, { data: sum }] = await Promise.all([
                api.get("/rams/requests", { params: q }),
                api.get("/rams/requests/summary", { params }),
            ]);
            setRows(list);
            setSummary(sum);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load RAMS requests");
        }
        setLoading(false);
    }, [filters.status, filters.overdue, presetClientId, presetProjectId]);

    useEffect(() => { refresh(); }, [refresh]);

    const issue = async (id) => {
        try {
            const { data } = await api.post(`/rams/requests/${id}/issue`);
            toast.success(`${data.reference} issued`);
            refresh();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Issue failed"); }
    };
    const duplicate = async (id) => {
        try {
            const { data } = await api.post(`/rams/requests/${id}/duplicate`);
            toast.success(`Duplicated as ${data.reference}`);
            refresh();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Duplicate failed"); }
    };
    const archive = async (id) => {
        if (!window.confirm("Archive this RAMS request?")) return;
        try {
            await api.post(`/rams/requests/${id}/archive`);
            toast.success("Archived");
            refresh();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not archive"); }
    };

    return (
        <div data-testid="rams-dashboard-page" className="space-y-5">
            {!compact && (
                <div className="flex items-end justify-between flex-wrap gap-3">
                    <div>
                        <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                            style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>RAMS</span>
                        <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">RAMS Requests</h2>
                    </div>
                    {canEdit && (
                        <div className="flex items-center gap-2">
                            <button data-testid="rams-generate-direct-btn" onClick={() => setDirectOpen(true)}
                                className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg border bg-white text-[#1F1F1F] hover:bg-[#F6F4F0]"
                                style={{ borderColor: "#E5E0DA" }}>
                                <Sparkles size={15} /> Generate directly
                            </button>
                            <button data-testid="rams-new-request-btn" onClick={() => setIssueOpen(true)}
                                className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white"
                                style={{ background: "var(--brand-accent, #C75A1B)" }}>
                                <Plus size={15} /> New RAMS request
                            </button>
                        </div>
                    )}
                </div>
            )}
            {compact && canEdit && (
                <div className="flex justify-end gap-2">
                    <button data-testid="rams-generate-direct-btn-compact" onClick={() => setDirectOpen(true)}
                        className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg border bg-white text-[#1F1F1F] hover:bg-[#F6F4F0]"
                        style={{ borderColor: "#E5E0DA" }}>
                        <Sparkles size={15} /> Generate directly
                    </button>
                    <button data-testid="rams-new-request-btn-compact" onClick={() => setIssueOpen(true)}
                        className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        <Plus size={15} /> New RAMS request
                    </button>
                </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                {CARDS.map(([key, label]) => (
                    <button key={key} data-testid={`rams-summary-card-${key}`}
                        onClick={() => setFilters((f) => ({ ...f, status: "", overdue: key === "overdue" ? !f.overdue : "" }))}
                        className={`text-left p-3 rounded-xl border bg-white ${key === "overdue" && summary.overdue > 0 ? "border-red-300" : "border-[#E5E0DA]"}`}>
                        <p className={`text-2xl font-bold ${key === "overdue" && summary[key] > 0 ? "text-red-600" : "text-[#1F1F1F]"}`}>{summary[key] ?? 0}</p>
                        <p className="text-[11px] text-[#6F6A64] font-medium leading-tight mt-0.5">{label}</p>
                    </button>
                ))}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
                <select data-testid="rams-filter-status" value={filters.status}
                    onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                    className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                    <option value="">All statuses</option>
                    {Object.keys(STATUS_TONE).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>

            <div className="rounded-2xl bg-white border border-[#E5E0DA] overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-[#F5F3F0] text-left text-[11px] uppercase tracking-wider text-[#6F6A64] border-b border-[#E5E0DA]">
                        <tr>
                            <th className="px-4 py-3 font-semibold">Reference</th>
                            {!presetClientId && <th className="px-4 py-3 font-semibold">Client</th>}
                            {!presetProjectId && <th className="px-4 py-3 font-semibold">Project</th>}
                            <th className="px-4 py-3 font-semibold">Template</th>
                            <th className="px-4 py-3 font-semibold">Due</th>
                            <th className="px-4 py-3 font-semibold">Status</th>
                            <th className="px-4 py-3 font-semibold w-44"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={7} className="text-center py-10 text-[#9A968F]"><Loader2 size={16} className="inline animate-spin mr-2" />Loading…</td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan={7} className="text-center py-10 text-[#9A968F]">No RAMS requests yet.</td></tr>
                        ) : rows.map((r) => (
                            <tr key={r.id} data-testid={`rams-request-row-${r.id}`} className="border-b border-[#F0EDE8] hover:bg-[#F5F3F0]">
                                <td className="px-4 py-3 font-semibold text-[#1F1F1F] cursor-pointer" onClick={() => setDetailId(r.id)}>
                                    {r.reference}
                                    {r.is_overdue && <AlertTriangle size={12} className="inline ml-1.5 text-red-500" />}
                                    <div className="text-[11px] text-[#9A968F] font-normal">{r.title}</div>
                                </td>
                                {!presetClientId && <td className="px-4 py-3 text-[#6F6A64]">{r.client_name || "—"}</td>}
                                {!presetProjectId && <td className="px-4 py-3 text-[#6F6A64]">{r.project_name || "—"}</td>}
                                <td className="px-4 py-3 text-[#6F6A64]">{r.template_name}</td>
                                <td className="px-4 py-3 text-[#6F6A64]">{r.due_date || "—"}</td>
                                <td className="px-4 py-3"><Pill tone={STATUS_TONE[r.status] || "neutral"}>{r.status}</Pill></td>
                                <td className="px-4 py-3">
                                    {canEdit && (
                                        <div className="flex items-center justify-end gap-2.5">
                                            {r.status === "Draft Request" && (
                                                <button data-testid={`rams-request-issue-${r.id}`} onClick={() => issue(r.id)}
                                                    className="inline-flex items-center gap-1 text-[12px] font-semibold text-[var(--brand-accent,_#C75A1B)] hover:underline"><Send size={11} />Issue</button>
                                            )}
                                            <button data-testid={`rams-request-duplicate-${r.id}`} onClick={() => duplicate(r.id)}
                                                className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#6F6A64] hover:text-[#1F1F1F] hover:underline"><Copy size={11} />Duplicate</button>
                                            {!["Approved", "Published to Client", "Client Acknowledged"].includes(r.status) && (
                                                <button data-testid={`rams-request-archive-${r.id}`} onClick={() => archive(r.id)}
                                                    className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#9A968F] hover:text-red-600 hover:underline"><ArchiveIcon size={11} />Archive</button>
                                            )}
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {issueOpen && (
                <RamsIssueDialog presetClientId={presetClientId} presetProjectId={presetProjectId}
                    onClose={() => setIssueOpen(false)} onDone={() => { setIssueOpen(false); refresh(); }} />
            )}
            {directOpen && (
                <RamsIssueDialog directMode presetClientId={presetClientId} presetProjectId={presetProjectId}
                    onClose={() => setDirectOpen(false)}
                    onDone={(data) => { setDirectOpen(false); refresh(); if (data?.id) setDetailId(data.id); }} />
            )}
            {detailId && (
                <RamsRequestDetail requestId={detailId} onClose={() => setDetailId(null)}
                    onChanged={refresh} canEdit={canEdit} />
            )}
        </div>
    );
}
